import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Button from "@/components/Button";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import api from "@/lib/api-client";
import { Mail, Loader2, AlertTriangle, CreditCard } from "lucide-react";

const GOOGLE_ENABLED = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [accountSuspended, setAccountSuspended] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);

  const { login, socialLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  const handleGoogleCredential = async (idToken: string) => {
    setError("");
    setEmailNotVerified(false);
    setAccountSuspended(false);
    setResendSuccess(false);
    setIsLoading(true);

    try {
      await socialLogin("google", idToken);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { data?: { message?: string; code?: string } };
      };
      const code = axiosError.response?.data?.code;
      if (code === "ACCOUNT_SUSPENDED") {
        setAccountSuspended(true);
        setError(
          axiosError.response?.data?.message ||
            "Tu cuenta ha sido suspendida por falta de pago.",
        );
      } else if (code === "NO_BUSINESS_ACCOUNT") {
        setError(
          "No hay ninguna cuenta con ese email de Google. Regístrate primero para crear tu negocio.",
        );
      } else {
        setError(
          axiosError.response?.data?.message ||
            "No se pudo iniciar sesión con Google. Inténtalo de nuevo.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      await api.post("/auth/resend-verification", { email });
      setResendSuccess(true);
    } catch {
      setError("Error al reenviar el email de verificación");
    } finally {
      setResendingEmail(false);
    }
  };

  const handleReactivateAccount = async () => {
    setIsReactivating(true);
    try {
      // Create reactivation checkout with credentials
      const response = await api.post("/stripe/reactivate", {
        email,
        password,
      });
      window.location.href = response.data.data.url;
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(
        axiosError.response?.data?.message ||
          "Error al iniciar la reactivación",
      );
    } finally {
      setIsReactivating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setEmailNotVerified(false);
    setAccountSuspended(false);
    setResendSuccess(false);
    setIsLoading(true);

    try {
      await login(email, password, rememberMe);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { data?: { message?: string; code?: string } };
      };

      // Check if the error is email not verified
      if (axiosError.response?.data?.code === "EMAIL_NOT_VERIFIED") {
        setEmailNotVerified(true);
        setError(
          axiosError.response.data.message ||
            "Debes verificar tu email antes de iniciar sesión.",
        );
      } else if (axiosError.response?.data?.code === "ACCOUNT_SUSPENDED") {
        setAccountSuspended(true);
        setError(
          axiosError.response.data.message ||
            "Tu cuenta ha sido suspendida por falta de pago.",
        );
      } else {
        const errorMessage =
          axiosError.response?.data?.message ||
          "Error al iniciar sesión. Verifica tus datos e inténtalo de nuevo.";
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img
            src="/logo-horizontal.png"
            alt="ReservaManager"
            className="h-10 w-auto mx-auto mb-4"
          />
          <h2 className="text-center text-2xl font-semibold text-gray-900">
            Iniciar sesión
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Gestiona las reservas de tu restaurante
          </p>
        </div>

        <div className="bg-white shadow-md rounded-lg p-8">
          {/* Email verification required message */}
          {emailNotVerified && !resendSuccess && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-amber-800 text-sm font-medium">
                    Email no verificado
                  </p>
                  <p className="text-amber-700 text-sm mt-1">{error}</p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendingEmail}
                    className="mt-3 inline-flex items-center text-sm font-medium text-amber-700 hover:text-amber-900 disabled:opacity-50"
                  >
                    {resendingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Reenviar email de verificación
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Resend success message */}
          {resendSuccess && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm">
                ✓ Email de verificación reenviado. Revisa tu bandeja de entrada
                (y spam).
              </p>
            </div>
          )}

          {/* Account suspended message */}
          {accountSuspended && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-800 text-sm font-medium">
                    Cuenta suspendida
                  </p>
                  <p className="text-red-700 text-sm mt-1">
                    Tu cuenta ha sido suspendida por falta de pago. Para
                    reactivarla, actualiza tu método de pago y realiza el pago
                    pendiente.
                  </p>
                  <button
                    type="button"
                    onClick={handleReactivateAccount}
                    disabled={isReactivating}
                    className="mt-3 inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    {isReactivating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Reactivar cuenta
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Generic error message */}
          {error && !emailNotVerified && !accountSuspended && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Correo electrónico
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Contraseña
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Recordarme
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
            </Button>
          </form>

          {GOOGLE_ENABLED && (
            <>
              <div className="my-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  o continúa con
                </span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
              <GoogleSignInButton
                onCredential={handleGoogleCredential}
                disabled={isLoading}
              />
            </>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              ¿No tienes una cuenta?{" "}
              <Link
                to="/register"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Regístrate gratis
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
